import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  TrendingUp,
  Calendar,
  ArrowRight,
  Loader2,
  RefreshCw,
  Target
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import RefreshButton from "@/components/v2/RefreshButton";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { isDeptHead } = useAuth();
  const [stats, setStats] = useState(null);
  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, rfpsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/rfp/list`)
      ]);
      setStats(statsRes.data);
      setRfps(rfpsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "analyzed": return "bg-emerald-100 text-emerald-700";
      case "processing": return "bg-amber-100 text-amber-700";
      case "uploaded": return "bg-blue-100 text-blue-700";
      case "failed": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading && !stats) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0F172A] font-['Manrope'] tracking-tight">
            RFP Dashboard
          </h1>
          <p className="text-[#64748B] mt-1">
            Monitor and analyze your Request for Proposals
          </p>
        </div>
        <div className="flex gap-3">
          <RefreshButton onClick={fetchData} data-testid="refresh-btn" className="gap-2" />
          {isDeptHead() && (
            <Link to="/upload">
              <Button
                className="bg-[#0F172A] hover:bg-[#1E293B] gap-2"
                data-testid="upload-new-btn"
              >
                <FileText className="w-4 h-4" />
                Upload New RFP
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="border-[#E2E8F0]" data-testid="stat-total">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#64748B] mb-1">
                  Total RFPs
                </p>
                <p className="text-4xl font-extrabold font-['Manrope'] text-[#0F172A]">
                  {stats?.total_rfps || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#007AFF]/10 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#007AFF]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]" data-testid="stat-analyzed">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#64748B] mb-1">
                  Analyzed
                </p>
                <p className="text-4xl font-extrabold font-['Manrope'] text-[#10B981]">
                  {stats?.analyzed_rfps || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]" data-testid="stat-processing">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#64748B] mb-1">
                  Processing
                </p>
                <p className="text-4xl font-extrabold font-['Manrope'] text-amber-600">
                  {stats?.processing_rfps || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]" data-testid="stat-pending">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#64748B] mb-1">
                  High Priority
                </p>
                <p className="text-4xl font-extrabold font-['Manrope'] text-[#007AFF]">
                  {stats?.high_priority_rfps || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#007AFF]/10 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-[#007AFF]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent RFPs */}
        <div className="lg:col-span-2">
          <Card className="border-[#E2E8F0]" data-testid="recent-rfps-card">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="font-['Manrope'] text-lg font-bold">
                Recent RFPs
              </CardTitle>
              <Link to="/upload">
                <Button variant="ghost" size="sm" className="text-[#007AFF] hover:text-[#0066DD]">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {rfps.length === 0 ? (
                <div className="text-center py-12" data-testid="empty-rfps">
                  <FileText className="w-16 h-16 mx-auto text-[#E2E8F0] mb-4" />
                  <p className="text-lg font-semibold text-[#0F172A] mb-2">
                    No RFPs uploaded yet
                  </p>
                  <p className="text-[#64748B] mb-4">
                    Upload your first RFP to get started with analysis
                  </p>
                  {isDeptHead() && (
                    <Link to="/upload">
                      <Button className="bg-[#007AFF] hover:bg-[#0066DD]">
                        Upload RFP
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {rfps.slice(0, 5).map((rfp) => (
                    <Link 
                      key={rfp.id} 
                      to={`/rfp/${rfp.id}`}
                      data-testid={`rfp-item-${rfp.id}`}
                    >
                      <div className="flex items-center gap-4 p-4 bg-white border border-[#E2E8F0] rounded-lg hover:border-[#007AFF] transition-all cursor-pointer group">
                        <div className="w-12 h-12 bg-[#F1F5F9] rounded-lg flex items-center justify-center group-hover:bg-[#007AFF]/10 transition-colors">
                          <FileText className="w-6 h-6 text-[#007AFF]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#0F172A] truncate">
                            {rfp.name}
                          </p>
                          <div className="flex items-center gap-3 text-sm text-[#64748B]">
                            <span>{formatFileSize(rfp.file_size)}</span>
                            <span>•</span>
                            <span>{rfp.total_pages || 0} pages</span>
                            <span>•</span>
                            <span>{formatDate(rfp.created_at)}</span>
                          </div>
                          {rfp.status === "processing" && rfp.processing_progress > 0 && (
                            <div className="mt-2">
                              <Progress value={rfp.processing_progress} className="h-1.5" />
                              <p className="text-xs text-[#64748B] mt-1">
                                Processing: {rfp.processing_progress}%
                              </p>
                            </div>
                          )}
                        </div>
                        <Badge className={getStatusColor(rfp.status)}>
                          {rfp.status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Deadlines */}
        <div>
          <Card className="border-[#E2E8F0]" data-testid="deadlines-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#FF3B30]" />
                Key Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.upcoming_deadlines?.length > 0 ? (
                <div className="space-y-3">
                  {stats.upcoming_deadlines.slice(0, 5).map((deadline, idx) => (
                    <div 
                      key={idx}
                      className="p-3 bg-red-50 border border-red-100 rounded-lg"
                      data-testid={`deadline-${idx}`}
                    >
                      <p className="font-mono font-semibold text-[#FF3B30] text-sm">
                        {deadline.date}
                      </p>
                      <p className="text-sm text-[#0F172A] mt-1 truncate">
                        {deadline.rfp_name}
                      </p>
                      <p className="text-xs text-[#64748B] mt-1 truncate">
                        {deadline.context}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto text-[#E2E8F0] mb-3" />
                  <p className="text-sm text-[#64748B]">
                    No deadlines detected yet
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-[#E2E8F0] mt-6" data-testid="quick-actions-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Manrope'] text-lg font-bold">
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {isDeptHead() && (
                <Link to="/upload" className="block">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 h-12"
                    data-testid="quick-upload-btn"
                  >
                    <FileText className="w-5 h-5 text-[#007AFF]" />
                    Upload New RFP
                  </Button>
                </Link>
              )}
              <Link to="/chat" className="block">
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-3 h-12"
                  data-testid="quick-chat-btn"
                >
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Ask AI Assistant
                </Button>
              </Link>
              <Link to="/tenders" className="block">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-12"
                  data-testid="quick-matrix-btn"
                >
                  <Target className="w-5 h-5 text-[#007AFF]" />
                  Browse tenders
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
